export const formatDate = (dateString: string, i18n: any) =>
    new Date(dateString).toLocaleDateString(i18n.language, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
    });

/**
 * A bare ISO date ("2026-03-10") as "Tue, 10 Mar".
 *
 * The `T00:00:00` is load-bearing. `new Date("2026-03-10")` is parsed as *UTC* midnight by
 * spec, so anywhere west of Greenwich it formats as the day before -- a Monday column
 * headed Sunday. The same string with a time on it is read as local, and the date survives.
 */
export const formatDayLabel = (isoDate: string, i18n: any): string =>
    formatDate(`${isoDate}T00:00:00`, i18n);

/**
 * A date on a record, as opposed to a date in the near future.
 *
 * Separate from formatDate because that one is built for appointments -- it leads with
 * the weekday and leaves the year out, which reads well for "Mon, Aug 4" next week and
 * badly for a clinical history where the whole question is often which year a sheet is
 * from.
 */
export const formatRecordDate = (dateString: string, i18n: any) =>
    new Date(dateString).toLocaleDateString(i18n.language, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });

/**
 * A moment on a record: the day and the clock time it started, as "21 Jan 2026, 13:00".
 *
 * The API sends these with no zone on them -- a session started at 13:00 at the clinic --
 * and the clock time is read straight off the string rather than through Date, which
 * would re-express it in the viewer's own zone and turn a 13:00 session into an 11:00
 * one. The date half is safe to parse: a value with a time in it is read as local.
 */
export const formatRecordDateTime = (dateTimeString: string, i18n: any) =>
    `${formatRecordDate(dateTimeString, i18n)}, ${dateTimeString.slice(11, 16)}`;

/**
 * The current moment as an `<input type="datetime-local">` value ("2026-01-21T13:00").
 *
 * Built out of the local parts rather than via toISOString, which converts to UTC and
 * would hand the therapist a start time an hour or two off their own clock.
 */
export const toDateTimeInputValue = (date: Date): string => {
    const pad = (part: number): string => String(part).padStart(2, '0');

    return (
        `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
        `T${pad(date.getHours())}:${pad(date.getMinutes())}`
    );
};

/** The same, for an `<input type="date">` ("2026-01-21"). */
export const toDateInputValue = (date: Date): string => toDateTimeInputValue(date).slice(0, 10);

/**
 * An ISO date shifted by whole days.
 *
 * Done on the calendar parts rather than by adding milliseconds, which is what makes it
 * survive a daylight-saving boundary: the day after 2026-03-28 is 2026-03-29 whether that
 * night is 23 hours long or 25. `Date` normalises an out-of-range day-of-month itself, so
 * the 31st plus one is the 1st of the next month with no arithmetic here.
 */
export const addDays = (isoDate: string, days: number): string => {
    const [year, month, day] = isoDate.split('-').map(Number);

    return toDateInputValue(new Date(year, month - 1, day + days));
};

/**
 * The Monday of the week an ISO date falls in.
 *
 * Weeks start on Monday because the clinic's do, and because a Romanian calendar puts
 * Sunday at the end. `getDay()` disagrees -- it calls Sunday 0 -- so a Sunday has to go
 * back six days rather than forward one, which is the whole reason this is a function and
 * not an inline subtraction.
 */
export const startOfWeek = (isoDate: string): string => {
    const [year, month, day] = isoDate.split('-').map(Number);
    const weekday = new Date(year, month - 1, day).getDay();

    return addDays(isoDate, weekday === 0 ? -6 : 1 - weekday);
};

export const formatTime = (dateString: string, i18n: any) =>
    new Date(dateString).toLocaleTimeString(i18n.language, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: false,
    });

/**
 * A wall-clock time from the API ("08:00:00") as an `<input type="time">` value ("08:00").
 *
 * These are clock times with no date and no zone, so they must never go through Date --
 * doing so would shift them by the viewer's offset.
 */
export const toTimeInputValue = (apiTime?: string | null): string =>
    apiTime ? apiTime.slice(0, 5) : '';

/**
 * The reverse: "08:00" as the API's "08:00:00".
 *
 * An empty field yields undefined rather than "", so an untouched optional time is left
 * out of the request entirely -- sending "" would fail to parse as a LocalTime.
 */
export const toApiTime = (inputValue?: string | null): string | undefined =>
    inputValue ? `${inputValue}:00` : undefined;
/**
 * Adds minutes to a local wall-clock string, staying in wall-clock throughout.
 *
 * The API's date-times carry no zone: `2026-03-10T09:00:00` is nine o'clock at the clinic,
 * whoever is looking. Parsing one into a `Date` and formatting it back would push it
 * through the viewer's offset and quietly move the appointment, which is why the arithmetic
 * is done on a zone-less basis and re-emitted in the same shape.
 */
export const addMinutes = (wallClock: string, minutes: number): string => {
    const [datePart, timePart = '00:00:00'] = wallClock.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hours, mins] = timePart.split(':').map(Number);

    // Constructed and read back in local time, so the two cancel out exactly.
    const shifted = new Date(year, month - 1, day, hours, mins + minutes);

    return toDateTimeInputValue(shifted) + ':00';
};

/**
 * How many minutes one wall-clock string is after another.
 *
 * The inverse of {@link addMinutes} and zone-less for the same reason: both ends are the
 * clinic's own clock, so parsing them through the viewer's offset would be applying a
 * correction to two values that never needed one. Constructed and subtracted in local time,
 * where the offsets cancel exactly.
 *
 * What an appointment's own length is measured with — which is what a move has to preserve
 * and what the slot finder has to be asked for.
 */
export const minutesBetween = (from: string, to: string): number => {
    const at = (wallClock: string): number => {
        const [datePart, timePart = '00:00:00'] = wallClock.split('T');
        const [year, month, day] = datePart.split('-').map(Number);
        const [hours, mins] = timePart.split(':').map(Number);

        return new Date(year, month - 1, day, hours, mins).getTime();
    };

    return Math.round((at(to) - at(from)) / 60000);
};
